import { FieldArray } from 'formik';
import React, { useState } from 'react';
import styled from '../../../components/styled';
import { IconButton } from '../../../components/paper';
import Icon from '../../../components/Icon';

import * as Colors from '../../../helpers/Colors';
import { Accordion, ActionButton, RowTitle } from '../../../components';
import { Event, trackEvent } from '../../../services/Analytics';

import {
  ControlType,
  Snippet,
  SnippetConfigurationColumn,
  SnippetDataDictionary,
} from '../../../services/dataModels';
import { DataRow } from './DataRow';
import { displayConfirmationAlert, formatControlValue } from '../../../helpers/Functions';
import { selectEditingMode } from '../../../store/selectors';

const createNewRow = (rowConfiguration: SnippetConfigurationColumn[]): SnippetDataDictionary => {
  const row = {};
  rowConfiguration.map((column) => {
    switch (column.controlType) {
      case 'Checkbox':
        row[column.name] = 'False';
        break;
      case 'Numeric':
      case 'Static Text':
      case 'Text':
      case 'Text - Single Line':
        row[column.name] = '';
        break;
      case 'Date':
      case 'Time':
      case 'DateTime':
        row[column.name] = '';
        break;
      case 'Dropdown':
        row[column.name] = null;
        break;
      default:
        break;
    }
  });
  trackEvent(Event.AddRowToSnippet);
  return row;
};

interface DataEntryTableSnippetProps {
  path: string;
  snippet: Snippet;
  data: SnippetDataDictionary[];
}

const DataEntryTableSnippet = ({ path, snippet, data }: DataEntryTableSnippetProps) => {
  const readOnly = selectEditingMode.useHook() === 'readOnly';
  const canAddDeleteRows = `${snippet.canAddDeleteRows}`.trim().toLocaleUpperCase() === 'TRUE';
  const canDelete = canAddDeleteRows && !readOnly;
  const canAdd = canAddDeleteRows && snippet.data[0].rowData.length < snippet.maxRows && !readOnly;
  const [expandNewItemIndex, setExpandNewItemIndex] = useState<number>();

  const header = (title: string, titleControlType: ControlType, onRemove?: () => void) => {
    return (expanded: boolean, canCollapse: boolean) => {
      if (!canCollapse) {
        return (
          <Container
            style={{
              alignSelf: 'center',
            }}>
            {onRemove && canDelete && (
              <DeleteButton icon="delete-forever" onPress={onRemove} size={23} animated={true} />
            )}
          </Container>
        );
      }

      return (
        <Container>
          <RowTitle title={formatControlValue(titleControlType, title)} />
          <TrailingText>
            <Icon
              name={expanded ? 'chevron-up' : 'chevron-down'}
              size={20}
              color={canCollapse ? '#00A3E0' : '#ccc'}
            />
            {onRemove && canDelete && (
              <DeleteButton icon="delete-forever" onPress={onRemove} size={23} animated={true} />
            )}
          </TrailingText>
        </Container>
      );
    };
  };

  const removeRow = (rowTitle: string, onConfirm: () => void) => {
    displayConfirmationAlert({
      title: 'Confirm Deletion',
      description: `Are you sure you want to delete ${rowTitle ? rowTitle : 'the row of data'}?`,
      onConfirm: () => {
        trackEvent(Event.RemoveRowFromSnippet);
        onConfirm();
      },
    });
  };

  return (
    <FieldArray name={`${path}.rowData`}>
      {({ push, remove }) => (
        <>
          {data.map((r, i) => {
            const title = r[snippet.configuration.rowTitleKey] as string;
            const titleIndex = snippet.configuration.columns.findIndex(
              (column) => column.name === snippet.configuration.rowTitleKey,
            );
            const titleControlType =
              snippet.type !== 'Matrix'
                ? snippet.configuration.columns[titleIndex].controlType
                : snippet.configuration.matrixCells.find(
                    (c) => c.colNumber === titleIndex + 1 && c.rowNumber === i + 1,
                  ).controlType;

            return (
              <RowAccordion
                key={i}
                title={''}
                size={snippet.configuration.columns.length}
                header={header(
                  title,
                  titleControlType,
                  canDelete && (() => removeRow(`${title}`, () => remove(i))),
                )}
                expanded={i === expandNewItemIndex}>
                <DataRow
                  path={`${path}.rowData[${i}]`}
                  configuration={snippet.configuration.columns}
                  data={r}
                />
              </RowAccordion>
            );
          })}
          {canAdd && (
            <AddButton
              icon="plus-circle-outline"
              onPress={() => {
                const newRow = createNewRow(snippet.configuration.columns);
                setExpandNewItemIndex(data.length);
                push(newRow);
              }}>
              Add New Entry
            </AddButton>
          )}
        </>
      )}
    </FieldArray>
  );
};

export default DataEntryTableSnippet;

const RowAccordion = styled(Accordion)``;

const Container = styled.View`
  flex-direction: row !important;
  align-items: flex-start;
  justify-content: flex-end;
  width: 100%;
  padding: 0px 20px;
`;

const TrailingText = styled.View`
  align-items: center;
  justify-content: center;
  flex-direction: row;
  margin-right: 10px;
`;

const DeleteButton = styled(IconButton).attrs({
  color: Colors.danger,
})`
  margin: -10px 0px;
  padding: 0px;
`;

const AddButton = styled(ActionButton)`
  margin-top: 10px;
`;
